// Écran de connexion : e-mail (adultes) ou code perso (mineures sans e-mail).
import { clubBrandById } from '../lib/api';
import { demoMode, demoSignIn, sendEmailCode, signInWithCode, verifyEmailCode, type DemoWho } from '../lib/auth';
import { clubHostSlug } from '../domain/slug';
import { $, html, mount } from './dom';
import { applyBrand, clubMark, storedBrand } from './theme';
import { dataHref, legalFootLink } from './legal-links';
import { renderLoginLine } from './official-partner';

type Mode = 'email' | 'code';
let mode: Mode = 'email';
let pendingEmail: string | null = null;

// L'app est celle du club (D-024) : son logo et son nom, la marque en petit.
const brand = () => {
  const club = storedBrand();
  return club ? html`
    <header class="login-brand">
      ${clubMark(club, 'lg')}
      <h1 class="club-name">${club.name}</h1>
      <p class="tag">L'app du club</p>
    </header>` : html`
    <header class="login-brand">
      <div class="ball" aria-hidden="true"></div>
      <h1>Arceau</h1>
      <p class="tag">L'app de ton club de basket</p>
    </header>`;
};

// Instance de test seulement : entrer dans le club de démo sans e-mail.
const DEMO: { who: DemoWho; label: string; sub: string }[] = [
  { who: 'admin', label: 'Bureau', sub: 'Nathalie, présidente : licences, événements, bénévoles' },
  { who: 'coach', label: 'Coach SF1', sub: 'Marc : planning, présences, plays' },
  { who: 'sarah', label: 'Joueuse adulte', sub: 'Sarah, SF1' },
  { who: 'parent', label: 'Parent', sub: 'Claire, mère de Kenza (U13)' },
  { who: 'kenza', label: 'Joueuse mineure', sub: 'Kenza, U13, code perso' },
  { who: 'partner', label: 'Partenaire', sub: 'Garage Saint-Jean : visibilité, appels du club' },
  { who: 'karim', label: 'Bénévole', sub: 'Karim, chef des cuistos' },
  { who: 'ines', label: 'Coach et joueuse', sub: 'Inès : joue en SF1, entraîne les U13' },
  { who: 'technique', label: 'Resp. technique', sub: 'Stéphane : toutes les équipes, côté sportif' },
];
// Avec des sous-domaines de clubs (production), la démo n'est proposée que sur celui du club de
// démo : l'app des vrais clubs ne montre jamais ces comptes en un clic.
const DEMO_SLUG = (import.meta.env.VITE_DEMO_SLUG as string | undefined)?.trim() || 'rivebelle';
const HOST_BASE = (import.meta.env.VITE_CLUB_HOST_BASE as string | undefined)?.trim();
const demoHere = demoMode && (!HOST_BASE || clubHostSlug(location.hostname, HOST_BASE) === DEMO_SLUG);
const demoBlock = () => demoHere ? html`
  <section class="login-card login-demo" aria-label="Instance de test">
    <p class="lead"><strong>Instance de test</strong> — entrer sans e-mail dans le club de démo :</p>
    <div class="demo-grid">
      ${DEMO.map(d => html`<button type="button" class="btn btn-ghost demo-btn" data-demo="${d.who}">
        <strong>${d.label}</strong><span class="muted small">${d.sub}</span></button>`)}
    </div>
    <p class="form-msg" role="alert" id="demo-msg"></p>
  </section>` : '';

function form() {
  if (mode === 'code') {
    return html`
      <form class="login-form" id="f-code" novalidate>
        <label class="field">
          <span>Identifiant</span>
          <input name="handle" autocomplete="username" autocapitalize="none" autocorrect="off" spellcheck="false"
                 placeholder="ex. kenza-4821" required>
        </label>
        <label class="field">
          <span>Code perso</span>
          <input name="code" type="password" inputmode="numeric" pattern="[0-9]*" maxlength="6"
                 autocomplete="current-password" placeholder="6 chiffres" required>
        </label>
        <p class="form-msg" role="alert"></p>
        <button class="btn btn-primary" type="submit">Se connecter</button>
        <p class="hint">Ton identifiant et ton code t'ont été donnés par ton coach ou tes parents.</p>
      </form>`;
  }
  if (pendingEmail) {
    return html`
      <form class="login-form" id="f-otp" novalidate>
        <p class="lead">Si <strong>${pendingEmail}</strong> est connue du club, un code à 6 chiffres vient d'y être envoyé.</p>
        <label class="field">
          <span>Code reçu par e-mail</span>
          <input name="token" inputmode="numeric" pattern="[0-9]*" maxlength="6" autocomplete="one-time-code"
                 placeholder="••••••" class="otp" required>
        </label>
        <p class="form-msg" role="alert"></p>
        <button class="btn btn-primary" type="submit">Se connecter</button>
        <button class="btn btn-link" type="button" id="b-change">Changer d'adresse</button>
      </form>`;
  }
  return html`
    <form class="login-form" id="f-email" novalidate>
      <label class="field">
        <span>Adresse e-mail</span>
        <input name="email" type="email" autocomplete="email" inputmode="email" placeholder="prenom@exemple.fr" required>
      </label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Recevoir mon code</button>
      <p class="hint">Pas de mot de passe : un code à usage unique arrive par e-mail.</p>
    </form>`;
}

// Le club gardé sur l'appareil est revérifié une fois par ouverture : renommé, nouveau logo,
// ou supprimé (on revient alors à Arceau). Hors ligne, on garde ce qu'on a.
let brandChecked = false;
async function refreshBrand(root: HTMLElement): Promise<void> {
  brandChecked = true;
  const b = storedBrand();
  // Marque d'une page de club (« host:rivebelle », D-032) ou valeur abîmée : pas un identifiant, rien à revérifier.
  if (!b || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(b.id)) return;
  let fresh: Awaited<ReturnType<typeof clubBrandById>>;
  try { fresh = await clubBrandById(b.id); } catch { return; }
  const same = fresh && fresh.name === b.name && fresh.short_name === b.short_name
    && fresh.logo_url === b.logo_url && fresh.primary_color === b.primary_color;
  if (same) return;
  applyBrand(fresh);
  if (root.querySelector('.login-brand')) renderLogin(root);
}

export function renderLogin(root: HTMLElement): void {
  if (!brandChecked) void refreshBrand(root);
  mount(root, html`
    <main class="login">
      ${brand()}
      <div class="po-login" id="login-offered"></div>
      <section class="login-card">
        <div class="seg" role="tablist" aria-label="Mode de connexion">
          <button role="tab" type="button" data-mode="email" aria-selected="${String(mode === 'email')}">Adresse e-mail</button>
          <button role="tab" type="button" data-mode="code" aria-selected="${String(mode === 'code')}">Code perso</button>
        </div>
        ${form()}
      </section>
      ${demoBlock()}
      <p class="login-foot">Pas encore de compte ? C'est ton club qui t'invite : demande à ton coach.</p>
      ${storedBrand() ? html`<p class="powered">propulsé par Arceau</p>` : ''}
      <p class="login-foot">${legalFootLink(dataHref())}</p>
    </main>`);

  // L'app offerte par un partenaire (D-147) : la ligne, sous le nom du club, sans compte.
  void renderLoginLine($(root, '#login-offered')!, storedBrand());
  root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach(b => b.addEventListener('click', () => {
    mode = b.dataset.mode as Mode;
    renderLogin(root);
  }));
  $(root, '#b-change')?.addEventListener('click', () => { pendingEmail = null; renderLogin(root); });
  root.querySelectorAll<HTMLButtonElement>('[data-demo]').forEach(b => b.addEventListener('click', async () => {
    b.disabled = true;
    const r = await demoSignIn(b.dataset.demo as DemoWho);
    if (!r.ok) { $(root, '#demo-msg')!.textContent = r.message; b.disabled = false; }
  }));

  const f = $<HTMLFormElement>(root, 'form')!;
  const msg = $(f, '.form-msg')!;
  const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
  $<HTMLInputElement>(f, 'input')?.focus();

  f.addEventListener('submit', async (e) => {
    e.preventDefault();
    const data = new FormData(f);
    msg.textContent = '';
    btn.disabled = true;
    try {
      if (f.id === 'f-email') {
        const r = await sendEmailCode(String(data.get('email') ?? ''));
        if (!r.ok) { msg.textContent = r.message; return; }
        pendingEmail = String(data.get('email')).trim().toLowerCase();
        renderLogin(root);
      } else if (f.id === 'f-otp') {
        const r = await verifyEmailCode(pendingEmail!, String(data.get('token') ?? ''));
        if (!r.ok) msg.textContent = r.message;
        else pendingEmail = null;   // la session déclenche l'affichage de l'app
      } else {
        const r = await signInWithCode(String(data.get('handle') ?? ''), String(data.get('code') ?? ''));
        if (!r.ok) msg.textContent = r.message;
      }
    } finally {
      btn.disabled = false;
    }
  });
}
